import React, { useMemo, useState } from "react";
import Select from "react-select";
import { API } from "aws-amplify";

import { Button, StyledInput } from "../components/BasicControls";
import { Sidebar, SidebarGroup } from "../components/Sidebar";
import { ContentWithSidebarPage } from "../components/Page";
import { FlexboxRows } from "../components/Layout";
import { Accordion } from "../components/Accordion";
import Bubble from "../components/Bubble";
import { handleError } from "../utilities/galileoApi";

// API endpoints are configured per environment (see aws-exports / Amplify config)
const apiEnv = process.env.REACT_APP_API_ENVIRONMENT;

// delete_stream is deployed in us-west-2 (382630417328), matching the dvcli
// gilgamesh default execution region.
const regionOptions = [
  { value: "us-west-2", label: "us-west-2" },
  { value: "us-east-1", label: "us-east-1" },
];

// Per-stream lifecycle. primary/pl streams are hibernated first, then deleted;
// bu/plbu backup streams are deleted directly (hibernate KeyErrors on them).
const PHASE = {
  PENDING: "Pending",
  HIBERNATING: "Hibernating",
  HIBERNATED: "Hibernated",
  DELETING: "Deleting",
  DELETED: "Deleted",
  FAILED: "Failed",
};

// Map each lifecycle phase onto a status the shared StatusIndicator already
// understands, so the badge is colored without touching that component:
//   STAGED  -> LightBlue   RUNNING -> LightBlue + spinner
//   WARNING -> Yellow       SUCCESS -> green
//   FAILURE -> red          (default) -> LightGray
const PHASE_STATUS = {
  [PHASE.PENDING]: "STAGED", // LightBlue, info
  [PHASE.HIBERNATING]: "RUNNING", // LightBlue, spinner
  [PHASE.HIBERNATED]: "WARNING", // Yellow (intermediate, not done yet)
  [PHASE.DELETING]: "RUNNING", // LightBlue, spinner
  [PHASE.DELETED]: "SUCCESS", // green, check
  [PHASE.FAILED]: "FAILURE", // red, warning triangle
};

// Step Function execution statuses returned by step_functions/read that mean
// "stop polling, this run is over".
const TERMINAL_FAILURE = ["FAILURE", "FAILED", "ERROR", "TIMED_OUT", "ABORTED"];

const POLL_INTERVAL_MS = 5000;

/**
 * Fetch the distinct stream names attached to a contribution stream id.
 * The backend (nexium -> orch_self_service.query_stream_names_by_cs_from_table)
 * already de-dupes, so the response is a flat list of unique stream-name strings.
 *
 * NOTE: the deployment-data table this queries only records the primary and
 * "pl" streams. The "bu"/"plbu" backup siblings are NOT in it, so they must be
 * discovered separately via the stream config (see expandStreamFamily).
 */
function fetchStreamNamesByCsid(csid) {
  return API.get(apiEnv, "nexium/get_stream_names/by_cs_id", {
    headers: { "Content-type": "application/json" },
    queryStringParameters: { cs_id: csid },
  });
}

/**
 * Resolve a stream's config. The response's elemental.shared_subregion map
 * names the stream's sibling variants (ha_proxy=primary, pl_ha_proxy=pl,
 * bu_ha_proxy=bu, pl_bu_ha_proxy=plbu) when they exist.
 */
function resolveStreamConfig(streamName) {
  return API.get(apiEnv, "stream_config/resolve", {
    headers: { "Content-type": "application/json" },
    queryStringParameters: { stream_name: streamName },
  });
}

/**
 * Given one stream name (a primary or pl stream from the CSID query), resolve
 * its config and return the full family: itself plus any siblings named in
 * elemental.shared_subregion. Each entry is { name, isBackup }.
 *
 * isBackup is derived from the shared_subregion ROLE KEY that named the stream:
 * bu_ha_proxy / pl_bu_ha_proxy are backups; ha_proxy / pl_ha_proxy (and the
 * seed stream itself) are not. Backups get delete-only; primary/pl get
 * hibernate -> delete (see processStream).
 *
 * Only names actually present in the config are returned, so we never fabricate
 * a non-existent bu/plbu stream. Falls back to just the (non-backup) seed if
 * the config or shared_subregion is unavailable.
 */
async function expandStreamFamily(streamName) {
  // seed comes from the CSID query -> always a primary/pl, never a backup
  const members = [{ name: streamName, isBackup: false }];
  try {
    const config = await resolveStreamConfig(streamName);
    if (config && typeof config === "object") {
      // Multi-encoder streams store the block as elemental_1, elemental_2, ...
      // so scan every "elemental*" key for a shared_subregion map.
      Object.keys(config).forEach((key) => {
        if (!key.startsWith("elemental")) return;
        const block = config[key];
        const sharedSubregion = block && block.shared_subregion;
        if (sharedSubregion && typeof sharedSubregion === "object") {
          // role -> name, e.g. bu_ha_proxy -> nascar...bu-iad-2
          Object.keys(sharedSubregion).forEach((role) => {
            const name = sharedSubregion[role];
            if (typeof name !== "string" || !name) return;
            members.push({ name, isBackup: role.includes("bu") });
          });
        }
      });
    }
  } catch (error) {
    // If we can't resolve the config, fall back to just this stream rather
    // than blocking the whole run. The primary+pl are still covered by the
    // CSID query; only its bu/plbu discovery is lost for this one stream.
    console.log(`Could not expand family for ${streamName}:`, error);
  }
  return members;
}

/**
 * Start a state machine for a single stream. Returns the execution ARN string.
 */
function startStepFunction(stateMachine, streamName, region) {
  return API.post(apiEnv, "step_functions/write", {
    headers: { "Content-Type": "application/json" },
    body: {
      action: "start_step_function",
      state_machine: stateMachine,
      region: region,
      stream_name: streamName,
    },
  });
}

/**
 * Read the status of a running execution.
 * Returns { status, timestamp, step, traceback }.
 */
function getExecutionStatus(executionArn) {
  return API.get(apiEnv, "step_functions/read", {
    headers: { "Content-Type": "application/json" },
    queryStringParameters: { action: "status", execution_arn: executionArn },
  });
}

/**
 * Poll an execution until it reaches a terminal state.
 * Resolves on SUCCEEDED, rejects with the traceback/status on failure.
 */
function pollUntilComplete(executionArn) {
  return new Promise((resolve, reject) => {
    const tick = () => {
      getExecutionStatus(executionArn)
        .then((res) => {
          const status = (res.status || "").toUpperCase();
          if (status === "SUCCEEDED") {
            resolve(res);
          } else if (TERMINAL_FAILURE.includes(status)) {
            reject(res.traceback || `Execution ${status.toLowerCase()}`);
          } else {
            // RUNNING (or anything non-terminal) -> keep polling
            setTimeout(tick, POLL_INTERVAL_MS);
          }
        })
        .catch((err) => reject(handleError(err)));
    };
    tick();
  });
}

export default function DeleteStreamsByCsid() {
  const [csid, setCsid] = useState("");
  const [region, setRegion] = useState(regionOptions[0]);

  const [streams, setStreams] = useState([]); // [{ name, isBackup, phase, hibernateArn, deleteArn, error }]
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [running, setRunning] = useState(false);

  // Typed confirmation gate — this tears down production infrastructure.
  const [confirmText, setConfirmText] = useState("");

  const patchStream = (name, patch) =>
    setStreams((prev) =>
      prev.map((s) => (s.name === name ? { ...s, ...patch } : s))
    );

  const stats = useMemo(() => {
    const counts = {
      total: streams.length,
      backups: 0,
      deleted: 0,
      failed: 0,
      inFlight: 0,
    };
    streams.forEach((s) => {
      if (s.isBackup) counts.backups += 1;
      if (s.phase === PHASE.DELETED) counts.deleted += 1;
      else if (s.phase === PHASE.FAILED) counts.failed += 1;
      else if (s.phase === PHASE.HIBERNATING || s.phase === PHASE.DELETING)
        counts.inFlight += 1;
    });
    return counts;
  }, [streams]);

  async function handleFetchStreams() {
    if (!csid.trim()) return;
    setFetching(true);
    setFetchError("");
    setStreams([]);
    setConfirmText("");
    try {
      const response = await fetchStreamNamesByCsid(csid.trim());
      const baseNames = Array.isArray(response) ? response : [];
      if (baseNames.length === 0) {
        setFetchError("No streams found for that CSID.");
        return;
      }

      // The CSID query only returns primary + pl streams. Resolve each one's
      // config to pull in its bu/plbu siblings (tagged isBackup), then dedupe
      // by name. If a name shows up as both backup and non-backup, prefer
      // non-backup (safer: it gets the full hibernate -> delete path).
      const families = await Promise.all(
        baseNames.map((name) => expandStreamFamily(name))
      );
      const byName = new Map();
      [].concat(...families).forEach(({ name, isBackup }) => {
        if (!byName.has(name)) byName.set(name, isBackup);
        else if (!isBackup) byName.set(name, false);
      });

      const streamList = Array.from(byName.keys())
        .sort()
        .map((name) => ({
          name,
          isBackup: byName.get(name),
          phase: PHASE.PENDING,
          hibernateArn: null,
          deleteArn: null,
          error: null,
        }));
      setStreams(streamList);
    } catch (error) {
      setFetchError(handleError(error));
    } finally {
      setFetching(false);
    }
  }

  /**
   * Tear down a single stream.
   *
   * primary/pl streams: hibernate -> wait -> delete -> wait.
   * bu/plbu backup streams: delete only.
   *
   * Backups skip hibernate on purpose: hibernate_stream runs
   * disable_dagon_monitoring, which calls get_deployment_data(stream_name) and
   * raises KeyError when the stream has no deployment-data record. bu/plbu
   * streams have no such record, so hibernate blows up on them. delete_stream's
   * update_deployment_data inserts a record when missing, so delete is safe.
   */
  async function processStream(stream) {
    const { name, isBackup } = stream;
    try {
      if (!isBackup) {
        // --- Hibernate (primary/pl only) ---
        patchStream(name, { phase: PHASE.HIBERNATING, error: null });
        const hibernateArn = await startStepFunction(
          "hibernate_stream",
          name,
          region.value
        );
        patchStream(name, { hibernateArn });
        await pollUntilComplete(hibernateArn);
        patchStream(name, { phase: PHASE.HIBERNATED });
      }

      // --- Delete ---
      patchStream(name, { phase: PHASE.DELETING, error: null });
      const deleteArn = await startStepFunction(
        "delete_stream",
        name,
        region.value
      );
      patchStream(name, { deleteArn });
      await pollUntilComplete(deleteArn);
      patchStream(name, { phase: PHASE.DELETED });
    } catch (error) {
      patchStream(name, {
        phase: PHASE.FAILED,
        error: typeof error === "string" ? error : handleError(error),
      });
    }
  }

  async function handleRun() {
    if (running) return;
    setRunning(true);
    // Each stream runs its own teardown chain independently, mirroring how
    // `dvcli gilgamesh run` fans out executions in parallel. processStream
    // catches its own errors (patches to FAILED) and never rejects, so
    // Promise.all resolves once every chain settles.
    const targets = streams.filter(
      (s) => s.phase === PHASE.PENDING || s.phase === PHASE.FAILED
    );
    await Promise.all(targets.map((s) => processStream(s)));
    setRunning(false);
  }

  const confirmed = confirmText.trim() === csid.trim() && csid.trim() !== "";
  const canRun = confirmed && streams.length > 0 && !running && !fetching;

  return (
    <ContentWithSidebarPage sidebarWidth="24rem">
      <Sidebar>
        <SidebarGroup name="Contribution Stream ID">
          <StyledInput>
            <input
              type="text"
              placeholder="amzn1.dv.live.csid...."
              value={csid}
              onChange={(e) => setCsid(e.target.value)}
            />
          </StyledInput>
          <Button
            onClick={handleFetchStreams}
            disabled={fetching || running || !csid.trim()}
            style={{ marginTop: "10px" }}
          >
            {fetching ? "Fetching..." : "Fetch Streams"}
          </Button>
          {fetchError ? (
            <p style={{ color: "red", fontSize: "small" }}>{fetchError}</p>
          ) : null}
        </SidebarGroup>

        <SidebarGroup name="Execution Region">
          <Select
            options={regionOptions}
            value={region}
            onChange={(option) => setRegion(option)}
            isDisabled={running}
          />
        </SidebarGroup>

        <SidebarGroup name="Confirm & Run">
          <p style={{ fontSize: "small", color: "#a11" }}>
            This <b>deletes</b> {streams.length || 0} stream
            {streams.length === 1 ? "" : "s"} in production. To confirm, re-type
            the CSID below.
          </p>
          <StyledInput>
            <input
              type="text"
              placeholder="Re-type the CSID to confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={streams.length === 0 || running}
            />
          </StyledInput>
          <Button
            onClick={handleRun}
            disabled={!canRun}
            style={{ marginTop: "10px" }}
          >
            {running
              ? "Running..."
              : `Delete ${streams.length || ""} Stream${
                  streams.length === 1 ? "" : "s"
                }`}
          </Button>
        </SidebarGroup>

        {streams.length > 0 ? (
          <SidebarGroup name="Summary">
            <div style={{ fontSize: "small", lineHeight: "1.6" }}>
              <div>Total: {stats.total}</div>
              <div>
                Backups (bu/plbu): {stats.backups} — delete only; the rest are
                hibernated first
              </div>
              <div>In flight: {stats.inFlight}</div>
              <div style={{ color: "green" }}>Deleted: {stats.deleted}</div>
              <div style={{ color: "red" }}>Failed: {stats.failed}</div>
            </div>
          </SidebarGroup>
        ) : null}
      </Sidebar>

      <FlexboxRows>
        <h2>Delete Streams by CSID</h2>
        {streams.length === 0 ? (
          <p>
            Enter a contribution stream id and click <b>Fetch Streams</b> to
            list the streams that will be torn down. The list includes each
            stream's <b>bu</b>/<b>plbu</b> backup siblings, which are resolved
            from the stream config (they are not in the CSID deployment-data
            query). Primary and pl streams are <b>hibernated then deleted</b>;
            bu/plbu backups are <b>deleted directly</b> (hibernate fails on
            them).
          </p>
        ) : (
          <Accordion title={`Streams (${streams.length})`}>
            {streams.map((s) => (
              <Bubble
                key={s.name}
                data={{
                  name: s.name,
                  role: s.isBackup ? "backup" : "primary/pl",
                  phase: s.phase,
                }}
                headerKeys={["name", "role"]}
                status={PHASE_STATUS[s.phase]}
                enabled={true}
              >
                <div style={{ fontSize: "small" }}>
                  <p>
                    <b>Type:</b>{" "}
                    {s.isBackup
                      ? "bu/plbu backup — delete only"
                      : "primary/pl — hibernate then delete"}
                  </p>
                  <p>
                    <b>Phase:</b> {s.phase}
                  </p>
                  {s.hibernateArn ? (
                    <p>
                      <b>Hibernate execution:</b>{" "}
                      <code>{s.hibernateArn}</code>
                    </p>
                  ) : null}
                  {s.deleteArn ? (
                    <p>
                      <b>Delete execution:</b> <code>{s.deleteArn}</code>
                    </p>
                  ) : null}
                  {s.error ? (
                    <div>
                      <b style={{ color: "red" }}>Error</b>
                      <pre style={{ whiteSpace: "pre-wrap" }}>
                        <code>{s.error}</code>
                      </pre>
                    </div>
                  ) : null}
                </div>
              </Bubble>
            ))}
          </Accordion>
        )}
      </FlexboxRows>
    </ContentWithSidebarPage>
  );
}
